/*
 * Dashboard Module
 */

define([
  "marionette",
  "backbone",
  "configuration/properties",
  "messaging/messenger",
  "modules/dashboard/dashboardView",
  "modules/reportSplit/reports/singleReport/singleReportModule",
  "modules/dashboard/dashboardLearner/dashboardLearnerModule",
  "modules/dashboard/dashboardContents/dashboardContentsModule",
  "moment-timezone",
  "jquery",
  "underscore",
], function (
  Marionette,
  Backbone,
  Properties,
  Messenger,
  DashboardView,
  SingleReportModule,
  DashboardLearnerModule,
  DashboardContentsModule,
  Moment,
  $,
  _,
) {
  "use strict";

  const DashboardController = Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "dashboard";
      this.region = options.region;
      this.isAuthor = false;
      this.shortUsageVisible = false;
      this.longUsageVisible = false;
      this.escalationsVisible = false;
      this._setUpVents();
      this.router = new Router({
        controller: this,
      });
    },
    show: function () {
      this.shortUsageVisible = false;
      this.longUsageVisible = false;
      this.escalationsVisible = false;
      //only load this once
      if (!this.reports) {
        this.reports = Messenger.reqres.request("reports");
      }
      //set URL
      const kbId = Messenger.reqres.request("active:kb");
      Backbone.history.navigate(kbId + "/");
      document.title = $.t("dashboard.title") + " - " + Properties.applicationName;
      const isAuthor = Messenger.reqres.request("user:is:author", kbId);
      //Intentionally fetch it as a new collection.  We may be adding "All" to the touchpoints for the dashboard, and
      // we don't want "All" showing up everywhere (since in many places, the user can multi-select).
      let touchpoints = Messenger.reqres.request("fetch:kb:touchpoints", kbId);
      const locales = Messenger.reqres.request("kb:languages");
      const defaultTouchpoint = Messenger.reqres.request("default:touchpoint");
      const defaultLocale = Messenger.reqres.request("default:response:language");
      const canCreateTopic = Messenger.reqres.request("can:create:topic", kbId);
      const allowContentTab = Messenger.reqres.request("can:view:contentTab", kbId);
      const allowLearnerTab = Messenger.reqres.request("can:view:learnerTab", kbId);

      //start/end date
      const endDate = Moment().endOf("day"); // jshint ignore:line
      const startDate = Moment().subtract(1, "months").startOf("day"); // jshint ignore:line
      const momentFormat = Messenger.reqres.request("get:moment:date:format");

      const endDateFormatted = endDate.format(momentFormat);
      const startDateFormatted = startDate.format(momentFormat);

      touchpoints.onSynced(function () {
        locales.onSynced(function () {
          if (touchpoints.length > 1) {
            const allTouchpoints = new Backbone.Model({
              touchPoint: $.t("dashboard.allTouchpoints"),
              touchPointId: 42,
            });
            touchpoints.add(allTouchpoints, { at: 0 });
          }
          this.model = new Backbone.Model({
            allowContentTab: allowContentTab,
            allowLearnerTab: allowLearnerTab,
            canCreateTopic: canCreateTopic,
            isAuthor: isAuthor,
            touchpoints: touchpoints,
            locales: locales,
            defaultTouchpoint: defaultTouchpoint,
            defaultLocale: defaultLocale,
            startDate: startDateFormatted,
            endDate: endDateFormatted,
            startDateMoment: startDate,
            endDateMoment: endDate,
          });
          this.isAuthor = isAuthor;
          this.layout = new DashboardView.dashboardLayout({ model: this.model });
          //sub modules
          this.escalations = this.addSubModule(
            new SingleReportModule({ region: this.layout.escalationsPane }),
          );
          this.accuracy = this.addSubModule(
            new SingleReportModule({ region: this.layout.accuracyPane }),
          );
          this.shortUsage = this.addSubModule(
            new SingleReportModule({ region: this.layout.shortUsagePane }),
          );
          this.longUsage = this.addSubModule(
            new SingleReportModule({ region: this.layout.longUsagePane }),
          );
          this.top10 = this.addSubModule(
            new SingleReportModule({ region: this.layout.topTopicList }),
          );
          //PROBLEM: Originally, only added learner/contents module if the user had permissions to view them.
          //But if you are on the dashboard of a KB of which you are author, and switch to a KB where you aren't, it
          // gets to the close old modules check (Marionette.js line 330).  It detects whether you are in the same
          // module as previously by seeing if the el for array[0] of the submodules is the same as the el for
          // array[array.length/2] of the sub-modules. This works if the sub-modules are always the same.  But if the
          // dashboard can have varying numbers of sub-modules depending on your permissions that doesn't work.
          // SOLUTION: Always add the module, just don't always show it
          this.learner = this.addSubModule(
            new DashboardLearnerModule({ region: this.layout.learner }),
          );
          this.contents = this.addSubModule(
            new DashboardContentsModule({ region: this.layout.contents }),
          );
          //events handlers
          this.layout.on(
            "click:topics:button",
            function () {
              //we are reusing the vent to add a topic from the learner, however, we do not pass a transcript to apply to
              // the topic.
              Messenger.vent.trigger("add:learner:topic");
            },
            this,
          );

          //handler for changing the touchpoint for the dashboard reports
          this.layout.on(
            "change:touchpoint",
            function (touchpointId) {
              //The model controls how the dashboard displays.  It should get the literal ID, even if it's the dummy one
              // for "All"
              this.model.set("defaultTouchpoint", touchpointId);
              //For reports and the learner, they need the actual list of touchpoint IDs (unless we want a dummy one on
              // the server, which we probably don't
              let touchpoints;
              if (touchpointId === 42) {
                //dummy touchpoint indicating "all touchpoints".  Return all touchpoints.
                touchpoints = this.layout.getAllTouchpoints();
              } else {
                touchpoints = touchpointId;
              }
              this.showReports(touchpoints, this.model.get("defaultLocale"));
              if (this.isAuthor) {
                this.learner.show(touchpoints, this.model.get("defaultLocale"));
              }
            },
            this,
          );

          //handler for changing the locale for the dashboard reports
          this.layout.on(
            "change:locale",
            function (localeId) {
              this.model.set("defaultLocale", localeId);
              this.showReports(
                this.model.get("defaultTouchpoint"),
                this.model.get("defaultLocale"),
              ); // may not work for
              // tpID of "42"
              // (all)
              if (this.isAuthor) {
                this.learner.show(this.model.get("defaultTouchpoint"), localeId);
                this.contents.show(localeId);
              }
            },
            this,
          );

          //clicked on the quick create button to switch to the alternate quick create UI
          this.layout.on(
            "show:quick:create",
            function () {
              //when the user clicks on this button, switch their default login preference to show the quick create UI on
              // startup
              this._reconfigureDefaultHomePage();
              Messenger.vent.trigger("show:quick:create");
            },
            this,
          );

          //event handlers for clicking tabs (use 'once' instead of 'on' because we only need to load the tab's
          // contents once)
          this.layout.once(
            "show:short:usage",
            function () {
              this.shortUsage.showIndividualReport(this.shortUsageReport);
              this.shortUsageVisible = true;
            },
            this,
          );
          this.layout.once(
            "show:long:usage",
            function () {
              this.longUsage.showIndividualReport(this.longUsageReport);
              this.longUsageVisible = true;
            },
            this,
          );
          this.layout.once(
            "show:escalations",
            function () {
              this.escalations.showIndividualReport(this.escalationReport);
              this.escalationsVisible = true;
            },
            this,
          );

          this.layout.on(
            "show",
            function () {
              this._showSubViews();
            },
            this,
          );
          this.showRegion(this.layout);

          //code to automatically say that the dashboard is saved because it has nothing to save
          Messenger.onSave(function () {
            console.log("saving dashboard");
            Messenger.saveAttempted(true); //success
          }, this);
        }, this);
      }, this);
    },
    _reconfigureDefaultHomePage: function () {
      //get the users model and change the 'showQuickCreate' attribute to true for the active KB
      const user = Messenger.reqres.request("user");
      const kbId = Messenger.reqres.request("active:kb");
      user.toggleQuickCreateAsHomePage(kbId, true);
      //save the user model, but don't wait up for a response or error.  Missing this change is not
      //as problematic as making the UI wait for the request to be processed.
      user.saveModel({
        success: _.noop,
        error: _.noop,
      });
    },
    _setUpVents: function () {
      Messenger.vent.on(
        "show:dashboard",
        function () {
          this.show();
        },
        this,
      );

      Messenger.vent.on(
        "kb:changed",
        function () {
          Messenger.command.execute("change:menu", null);
          this._showQuickCreateOrDashboard();
        },
        this,
      );
    },

    _showSubViews: function () {
      //show buttons
      if (this.isAuthor) {
        const touchpointId = this.layout.model.get("defaultTouchpoint");
        this.learner.show(touchpointId, this.layout.model.get("defaultLocale"));
        this.contents.show(this.layout.model.get("defaultLocale"));
      }
      this.reports.onSynced(function () {
        const defaultTouchpoint = Messenger.reqres.request("default:touchpoint");
        const defaultLocale = Messenger.reqres.request("default:interface:language");
        this.showReports(defaultTouchpoint, defaultLocale);
      }, this);
    },
    showReports: function (touchpointID, localeID) {
      this.escalationReport = this.escalations.findReport(
        this.reports,
        "reports.escalationDashboard.name",
      );
      this.accuracyReport = this.accuracy.findReport(this.reports, "reports.accuracyFMeasure.name");
      this.shortUsageReport = this.shortUsage.findReport(this.reports, "dashboard7Days");
      this.longUsageReport = this.longUsage.findReport(this.reports, "dashboardUsageLong");
      this.top10report = this.top10.findReport(this.reports, "reports.top10trending.name");
      Messenger.reqres.setHandler(
        "param:{startDate}",
        function () {
          return this.model.get("startDateMoment").valueOf();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{startDate}",
        function () {
          return this.model.get("startDate");
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{endDate}",
        function () {
          return this.model.get("endDateMoment").valueOf();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{endDate}",
        function () {
          return this.model.get("endDate");
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{touchpoint}",
        function () {
          if (touchpointID === 42) {
            //dummy touchpoint indicating "all touchpoints".  Return all touchpoints.
            return this.layout.getAllTouchpoints();
          } else {
            return touchpointID;
          }
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{languages}",
        function () {
          return [localeID];
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{language}",
        function () {
          return localeID;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{timeZone}",
        function () {
          const timeZone = Moment.tz.guess();
          console.log("TIMEZONE: " + timeZone);
          return timeZone;
        },
        this,
      );
      Messenger.reqres.setHandler("param:{analyticsTags}", () => {
        return this.model.get("analyticsTags") || "";
      });

      this.accuracy.showIndividualReport(this.accuracyReport); //only load and show the report that's on the active tab
      this.top10.showIndividualReport(this.top10report);
      if (this.shortUsageVisible) {
        //changed touchpoint after opening short usage
        this.shortUsage.showIndividualReport(this.shortUsageReport);
      }
      if (this.longUsageVisible) {
        //changed touchpoint after opening long usage
        this.longUsage.showIndividualReport(this.longUsageReport);
      }
      if (this.escalationsVisible) {
        //changed touchpoint after opening short usage
        this.escalations.showIndividualReport(this.escalationReport);
      }
    },
    _route: function (kbId) {
      if (kbId === null) {
        console.log("kbId is null");
        //set kbId as default kbId for user
        kbId = Messenger.reqres.request("default:kbId");
      }
      //set active kb
      console.log("URL set active kb to " + kbId);
      Messenger.command.execute("set:active:kb", kbId);
      Messenger.command.execute("change:menu", null);
      //Send reviewers to Tasks instead of the dashboard
      if (
        Messenger.reqres.request("user:is:reviewer") &&
        !Messenger.reqres.request("user:is:reporter")
      ) {
        kbId = Messenger.reqres.request("active:kb");
        Backbone.history.navigate(kbId + "/reviewer", { trigger: true });
      } else {
        this._showQuickCreateOrDashboard();
      }
    },
    _showQuickCreateOrDashboard: function () {
      /**
       * At this point, we should make sure that we actually want to show the knowledge dashboard.
       * We get to this point if the user logs in without anything in the URL hash.  AKA, this is the
       * default route.  If the user should go to quick create first, we should make sure they go there now.
       * Of course, if the user is not an author, then they must go to the knowledge dashboard.
       */
      const kbId = Messenger.reqres.request("active:kb");
      const canCreateTopic = Messenger.reqres.request("can:create:topic", kbId);
      const showQuickCreate = canCreateTopic && Messenger.reqres.request("default:quick:create");
      const userIsAuthor = Messenger.reqres.request("user:is:author");
      if (showQuickCreate && userIsAuthor) {
        Messenger.vent.trigger("show:quick:create");
      } else {
        this.show();
      }
    },
  });

  const Router = Marionette.AppRouter.extend({
    appRoutes: {
      "(:kbId/)": "_route",
    },
  });

  return DashboardController;
});
